/**
 * Options for currency formatting.
 */
export interface FormatCurrencyOptions {
    /** Number of decimal places. Defaults to 2. */
    decimals?: number;
    /** Fallback string if value is invalid. Defaults to 'RM 0.00'. */
    fallback?: string;
    /** Whether to show the currency symbol. Defaults to true. */
    showSymbol?: boolean;
}

/**
 * Formats a number or numeric string into Malaysia Ringgit (RM) format.
 * 
 * @example
 * formatMYR(1000) -> "RM 1,000.00"
 * formatMYR("49.9") -> "RM 49.90"
 * formatMYR(null) -> "RM 0.00"
 */
export const formatMYR = (
    value: number | string | null | undefined,
    options: FormatCurrencyOptions = {}
): string => {
    const {
        decimals = 2,
        fallback = 'RM 0.00',
        showSymbol = true
    } = options;

    // 1. Handle null/undefined
    if (value === null || value === undefined) {
        return fallback;
    }

    // 2. Parse string to number if needed
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;

    // 3. Handle invalid numbers
    if (isNaN(numericValue)) {
        return fallback;
    }

    // 4. Use Intl.NumberFormat for robust formatting
    try {
        const formatter = new Intl.NumberFormat('en-MY', {
            style: showSymbol ? 'currency' : 'decimal',
            currency: 'MYR',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });

        let formatted = formatter.format(numericValue);

        // Some browsers/environments might format MYR as "MYR" or "RM".
        // We strictly want "RM" with a space if possible, or just the standard Intl output.
        // Intl.NumberFormat with 'en-MY' and 'MYR' typically gives "RM1,000.00".
        // We ensure there's a space if requested or just return the standard.
        if (showSymbol && !formatted.includes('RM')) {
            // Fallback if the locale doesn't support RM symbol
            return `RM ${numericValue.toLocaleString('en-MY', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            })}`;
        }

        // Standardize "RM" to "RM " (with space) for better readability if desired
        return formatted.replace('RM', 'RM ').trim();
    } catch (error) {
        console.warn(`[formatMYR] Failed to format value: ${value}`, error);
        return fallback;
    }
};
